import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  BRAND_API_MESSAGES,
  type BrandProfileView,
  type BrandSourceTag,
  type EditPageListKey,
  type WorkspaceNameConflict,
} from '@mg/shared';
import { actionErrorText, api, ApiRequestError, asWorkspaceNameConflict, isBrandVersionConflict } from '../../../lib/api';
import { useTenant } from '../../../auth/TenantContext';
import { useFooterSave } from '../../../components/settings/footerSave';
import { adoptBrandProfile, brandKeys } from '../../brand/queries';
import { useWebsiteField, websiteError, type WebsiteField } from '../../brand/website';
import {
  brandInfoPayload,
  isBrandInformationDirty,
  listProvenance,
  listsOf,
  sameFormValues,
  type BrandInformationForm,
} from './brandInfoPayload';

/** A failed save's message when the server gave none worth showing. */
export const SAVE_FAILED_TEXT = "Couldn't save your changes. Try again.";
/** A Reload after a version conflict that could not read the brand again. */
export const RELOAD_FAILED_TEXT = "Couldn't load your brand's latest details. Try again.";

/** Why the last save did not go through, as the page shows it. */
export type SaveProblem =
  /** 409 BRAND_VERSION_CONFLICT: changed in another tab or by another admin; Reload. */
  | { kind: 'conflict' }
  /** 409 name taken: the notice with the existing workspace's admins, under the name. */
  | { kind: 'name'; conflict: WorkspaceNameConflict }
  /** Anything else, in words. */
  | { kind: 'error'; message: string };

/**
 * What a failed brand save says (final-design §2.14: the server's sentence as is for a 400 or a 409).
 * A 401 is the session, not the input; anything else gets the plain retry sentence.
 */
export function saveErrorText(err: unknown): string {
  if (err instanceof ApiRequestError) {
    if (err.status === 401) return actionErrorText(err, SAVE_FAILED_TEXT);
    if ((err.status === 400 || err.status === 409) && err.message) return err.message;
  }
  return SAVE_FAILED_TEXT;
}

export function saveProblemOf(err: unknown): SaveProblem {
  const nameConflict = asWorkspaceNameConflict(err);
  if (nameConflict) return { kind: 'name', conflict: nameConflict };
  if (isBrandVersionConflict(err)) return { kind: 'conflict' };
  return { kind: 'error', message: saveErrorText(err) };
}

export interface BrandInformationFormState {
  name: string;
  setName: (next: string) => void;
  /** "Enter a name for your Brand Workspace." while the name is empty. */
  nameError: string | null;
  website: WebsiteField;
  /** The website's error: once it was left invalid, or as soon as the page has a change (O15). */
  websiteError: string | null;
  lists: Record<EditPageListKey, string[]>;
  setList: (key: EditPageListKey, next: string[]) => void;
  /** The tags a list shows: the stored ones, plus Manual Entry once it changed; none when empty. */
  listProvenance: (key: EditPageListKey) => { sources: BrandSourceTag[]; aiSuggested: boolean };
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  problem: SaveProblem | null;
  /** After a version conflict: read the brand again and start over from it (unsaved changes are lost). */
  reload: () => Promise<void>;
  reloading: boolean;
  /** A mutation of this page answered with a newer view (a dismissal): take its version. */
  adopt: (next: BrandProfileView) => void;
}

/**
 * The Brand information page's form (web.md §6.2): the workspace name and website, the five
 * Vocabulary lists and Writing guidelines, saved by the page's sticky footer (`brand-information`)
 * through PUT /tenants/:id/brand/information.
 *
 * The form keeps the view it was read from (`base`): the save sends that view's version, and compares
 * against its values, so a change made elsewhere since is refused (409) rather than overwritten. A
 * newer view replaces the base, and the values, only while the page has no changes; the page's own
 * mutations (the save, the two dismissals) hand theirs over, so it never refuses itself.
 */
export function useBrandInformationForm({
  view,
  tenantId,
  canEdit,
}: {
  view: BrandProfileView;
  tenantId: string;
  canEdit: boolean;
}): BrandInformationFormState {
  const queryClient = useQueryClient();
  const { refresh } = useTenant();
  const [base, setBase] = useState(view);
  const [name, setName] = useState(view.workspace.name);
  const website = useWebsiteField(view.workspace.websiteUrl ?? '');
  const [lists, setLists] = useState(() => listsOf(view));
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [problem, setProblem] = useState<SaveProblem | null>(null);

  const form: BrandInformationForm = { name, websiteUrl: website.value, lists };
  const dirty = isBrandInformationDirty(form, base);
  const nameError = name.trim() === '' ? BRAND_API_MESSAGES.NAME_REQUIRED : null;
  const websiteMessage = website.error ?? (dirty ? websiteError(website.value) : null);
  const canSave = canEdit && nameError === null && website.valid;

  const resetWebsite = website.reset;
  const seed = useCallback(
    (next: BrandProfileView) => {
      setBase(next);
      setName(next.workspace.name);
      resetWebsite(next.workspace.websiteUrl ?? '');
      setLists(listsOf(next));
    },
    [resetWebsite],
  );

  // A newer view (a refetch, another tab's change) re-seeds a page without changes. One with changes
  // keeps them and its base: its save is then refused as a conflict, and Reload starts over. Only a
  // NEWER one: right after this page's own save or dismissal the form already holds the answer, and
  // the query hands it over a tick later.
  useEffect(() => {
    if (view.version > base.version && !dirty) seed(view);
  }, [view, base.version, dirty, seed]);

  const adopt = useCallback(
    (next: BrandProfileView) => {
      if (!dirty) seed(next);
      // The mutation changed nothing on the form (a dismissal): keep the changes, take the version.
      else if (sameFormValues(base, next)) setBase(next);
    },
    [dirty, base, seed],
  );

  const save = async (): Promise<boolean> => {
    if (!canSave) return false;
    setSaving(true);
    setProblem(null);
    try {
      const next = await api.saveBrandInformation(tenantId, brandInfoPayload(form, base));
      adoptBrandProfile(queryClient, next);
      seed(next);
      // The name and website show in the workspace switcher and on other pages.
      await refresh();
      return true;
    } catch (err) {
      setProblem(saveProblemOf(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Back to the last saved values: the newest view the page has.
  const discard = () => {
    seed(view.version >= base.version ? view : base);
    setProblem(null);
  };

  const reload = async () => {
    setReloading(true);
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: brandKeys.profile(tenantId),
        queryFn: ({ signal }) => api.getBrandProfile(tenantId, signal),
        staleTime: 0,
      });
      seed(fresh);
      setProblem(null);
    } catch {
      setProblem({ kind: 'error', message: RELOAD_FAILED_TEXT });
    } finally {
      setReloading(false);
    }
  };

  useFooterSave({ id: 'brand-information', dirty, canSave, saving, save, discard });

  return {
    name,
    setName,
    nameError,
    website,
    websiteError: websiteMessage,
    lists,
    setList: (key, next) => setLists((prev) => ({ ...prev, [key]: next })),
    listProvenance: (key) => listProvenance(form, base, key),
    dirty,
    canSave,
    saving,
    problem,
    reload,
    reloading,
    adopt,
  };
}
